class SectionRecommended extends HTMLElement {
  
  constructor() {
    super();
    this.init();
  }

  init = () => {
    this.loadSection();
  }

  loadSection = () => {
    axios.get(this.dataset.url)
      .then((response) => {
        const doc = Utils.getDocument(response.data);
        const section = doc.querySelector('section-recommended');
        this.innerHTML = section.innerHTML;
        this.reinitializeCustomElements();
      })
      .catch((error) => {
        Utils.errorHanlder(error);
      })
      .finally(() => {});
  }

  reinitializeCustomElements = () => {
    // Dopo aver inserito il nuovo HTML nel DOM, va inizializzato SOLO lo swiper
    // esterno del carosello (figlio diretto di <section-recommended>), che ha
    // data-noinit e quindi non si auto-inizializza nel constructor.
    // Le gallery interne delle product-card sono <theme-swiper> annidati SENZA
    // data-noinit: si auto-inizializzano già quando l'HTML viene iniettato.
    // Re-inizializzarle qui creerebbe una doppia istanza Swiper su ognuna,
    // rompendo la gestione dei pointer event e bloccando il drag del carosello.
    const swiper = this.querySelector(':scope > theme-swiper');
    if (!swiper) return;

    swiper.querySelectorAll(':scope > theme-swiper-breakpoint').forEach(breakpoint => {
      customElements.upgrade(breakpoint);
    });
    customElements.upgrade(swiper);
    // Richiama init() per inizializzare il Swiper con le nuove configurazioni
    swiper.init();

    // Le gallery interne delle card (effetto fade, navigabili con le frecce) sono
    // <theme-swiper> annidati che catturano il drag orizzontale del puntatore,
    // impedendo di trascinare il carosello esterno. Le disattiviamo dal touch SOLO
    // qui dentro: così il carosello del carrello si trascina su tutta la superficie
    // della card, mentre le gallery delle card altrove restano swipabili.
    this.querySelectorAll('.swiper-slide theme-swiper').forEach((inner) => {
      if (inner.swiper) {
        inner.swiper.allowTouchMove = false;
      }
    });
  }
}
if (!window.customElements.get("section-recommended")) {
  window.customElements.define("section-recommended", SectionRecommended);
}

class ZendeskForm extends ThemeForm {
  fields; zendesk;
  static TICKET_URL = '/api/v2/requests';
  static ATTACHMENT_URL = '/api/v2/uploads';
  // Tempo minimo (ms) tra caricamento e invio: sotto questa soglia è quasi certamente un bot
  static MIN_FILL_TIME = 3000;
  static selectors = {
    successTemplate: '[data-success-template]',
    successMessage: '[data-success]',
    honeypot: '[data-honeypot]',
    recaptchaError: '[data-recaptcha-error]',
  }

  constructor () {
    super();
    this.zendesk = this.dataset.zendesk;
    this.successTemplate = this.querySelector(ZendeskForm.selectors.successTemplate);
    this.honeypot = this.querySelector(ZendeskForm.selectors.honeypot);
    this.recaptchaError = this.querySelector(ZendeskForm.selectors.recaptchaError);
    this.loadedAt = Date.now();
  }

  isBot = () => {
    // Honeypot compilato o invio troppo rapido
    const honeypotFilled = this.honeypot != undefined && this.honeypot.value !== '';
    return honeypotFilled || (Date.now() - this.loadedAt) < ZendeskForm.MIN_FILL_TIME;
  }

  recaptchaPassed = () => {
    // reCAPTCHA v2 lato browser: blocca l'invio finché la checkbox non è risolta.
    // Il token non viene verificato lato server (le richieste anonime Zendesk non lo supportano).
    if (this.dataset.recaptcha === undefined) return true;
    const passed = window.grecaptcha != undefined && window.grecaptcha.getResponse() !== '';
    if (this.recaptchaError != undefined) this.recaptchaError.hidden = passed;
    return passed;
  }

  resetRecaptcha = () => {
    if (this.dataset.recaptcha !== undefined && window.grecaptcha != undefined) {
      window.grecaptcha.reset();
    }
  }

  getFields = () => {
    // Estrae i dati dal form e li struttura secondo la API Zendesk
    const formData = new FormData(this.form);
    this.fields = {
      request: {
        subject: formData.get('subject'),
        requester: { name: formData.get('name'), email: formData.get('email') },
        comment: {
          body: formData.get('body'),
        }
      }
    };
    // Raccoglie i campi custom (definiti dal backend) e li aggiunge al ticket
    const customFields = [];
    formData.forEach((value, key) => {
      const matches = key.match(/^custom\[(.+)\]$/);
      if (matches) {
        customFields.push({ id: Number(matches[1]), value: value });
      }
    });
    if(customFields.length > 0){
      this.fields.request.custom_fields = customFields;
    }
    // Se ci sono allegati, c'è prima un upload su Zendesk, poi la creazione del ticket
    if(formData.get('attachment').size > 0) {
      this.uploadAttachment(formData.get('attachment'));
    } else {
      this.createTicket();
    }
  }

  uploadAttachment = (field) => {
    // Carica l'allegato su Zendesk (prima di creare il ticket)
    // Zendesk restituisce un token che viene allegato al ticket
    const url = `${this.zendesk}${ZendeskForm.ATTACHMENT_URL}?filename=${encodeURIComponent(field.name)}`;
    let config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: url,
      headers: {
        'Content-Type': field.type || 'application/octet-stream'
      },
      data : field
    };
    axios.request(config).then((response) => {
      if(response.data.upload.token != undefined){
        // Se l'upload ha successo, aggiunge il token agli uploads del ticket
        this.fields.request.comment.uploads = [response.data.upload.token];
        this.createTicket();
      } else {
        // Se fallisce, mostra un messaggio di errore input-specific
        this.form.attachment.closest('input-text').validateServer(this.dataset.fileError);
      }
    }).catch((error) => {
      this.dispatchEvent(new CustomEvent(Utils.E_FORM_ERROR, { detail: error }));
      this.form.attachment.closest('input-text').validateServer(this.dataset.fileError);
    });
  }

  createTicket = () => {
    this.dispatchEvent(new CustomEvent(Utils.E_FORM_SUBMIT));
    const url = `${this.zendesk}${ZendeskForm.TICKET_URL}`;
    axios.post(url, JSON.stringify(this.fields), {
      // Richiesta anonima: il requester è preso da request.requester
      headers: {
        'Content-Type': 'application/json',
        "Accept": "application/json"
      }
    })
    .then(response => {
      this.success(response.data.request.id);
      this.dispatchEvent(new CustomEvent(Utils.E_FORM_SUCCESS, { detail: response }));
    })
    .catch(error => {
      this.dispatchEvent(new CustomEvent(Utils.E_FORM_ERROR, { detail: error }));
      Utils.errorHanlder(error);
    })
    .finally(() => {
      this.hideLoader();
      this.dispatchEvent(new CustomEvent(Utils.E_FORM_SENT));
      this.form.reset();
      this.resetRecaptcha();
    });
  }

  ajaxSubmit = () => {
    if (this.isBot()) {
      // Scarta in silenzio, senza segnalare al bot che è stato riconosciuto
      this.hideLoader();
      this.form.reset();
      return;
    }
    if (!this.recaptchaPassed()) {
      this.hideLoader();
      return;
    }
    this.getFields();
  }

  success = (ticketId) => {
    if(this.successMessage != undefined){
      this.successMessage.remove();
    }
    const template = this.successTemplate.innerHTML.replaceAll('{ticket}', ticketId);
    this.insertAdjacentHTML('afterbegin', template);
    this.successMessage = this.querySelector(ZendeskForm.selectors.successMessage);
  }
}
if (!window.customElements.get("zendesk-form")) {
  window.customElements.define("zendesk-form", ZendeskForm);
}

class VideoPoster extends HTMLElement {
  frame; poster;
  
  constructor() {
    super();
    this.frame = this.querySelector('iframe');
    this.poster = this.querySelector('[data-poster]');
    this.init();
  }

  init = () => {
    if(this.poster != undefined){
      this.poster.addEventListener('click', () => this.playVideo());
    }
  }

  playVideo = () => {
    // Aggiunge il parametro autoplay all'URL dell'iframe
    // Defer il caricamento del video finché l'utente non clicca sul poster
    // Riduce il carico della pagina iniziale
    const url = new URL(this.frame.src);
    url.searchParams.set('autoplay', '1');
    this.frame.src = url.toString();
    this.poster.classList.add('d-none');
  }
}
if (!window.customElements.get("video-poster")) {
  window.customElements.define("video-poster", VideoPoster);
}

class CatalogSlides extends HTMLElement {
  #hashs = [];

  connectedCallback() {
    if(!window.customElements.get('bs-modal')) {
      window.customElements.whenDefined('bs-modal').then(() => {
        this.init();
      });
      return;
    }

    this.init();
  }

  init = () => {
    const modals = this.querySelectorAll('bs-modal');
    this.#hashs = Array.from(modals).map(modal => `#${modal.id}`);
    
    this.handleHashChange(); // Controlla l'hash all'inizio per mostrare il modal se necessario
    window.addEventListener('hashchange', this.handleHashChange.bind(this));

    this.nextArrow?.forEach(arrow => arrow.addEventListener('click', this.next.bind(this)));
    this.prevArrow?.forEach(arrow => arrow.addEventListener('click', this.prev.bind(this)));

    // All'atterraggio con un hash valido nell'URL, scrolla fino al link che apre la slide
    // così alla chiusura del popup l'utente si ritrova nel contesto da cui era partito
    this.scrollToTrigger(window.location.hash);
  }
  
  next = () => {
    const currentHash = window.location.hash;
    const currentIndex = this.#hashs.indexOf(currentHash);
    if(currentIndex === -1) return; // Se l'hash corrente non è nella lista, non fare nulla
    const nextIndex = (currentIndex + 1) % this.#hashs.length; // Passa al prossimo, torna al primo se è alla fine
    const nextHash = this.#hashs[nextIndex];
    history.replaceState(null, '', nextHash);
    window.dispatchEvent(new Event('hashchange'));
  }

  prev = () => {
    const currentHash = window.location.hash;
    const currentIndex = this.#hashs.indexOf(currentHash);
    if(currentIndex === -1) return;
    const prevIndex = (currentIndex - 1 + this.#hashs.length) % this.#hashs.length; // Passa al precedente, torna all'ultimo se è al primo
    const prevHash = this.#hashs[prevIndex];
    history.replaceState(null, '', prevHash);
    window.dispatchEvent(new Event('hashchange'));
  }

  handleHashChange = () => {
    const currentHash = window.location.hash;
    if(this.#hashs.includes(currentHash)){
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    if(!currentHash || !this.#hashs.includes(currentHash)) return;
    const notTargetModals = this.querySelectorAll('bs-modal:not(' + currentHash + ')');
    notTargetModals.forEach(modal => {
      modal.hide();
    });
  }

  scrollToTrigger = (hash) => {
    // Cerca nella pagina il link che apre questa slide (es. <a href="#catalog-xxx">)
    // ed esclude eventuali anchor interni al popup, così la pagina sottostante
    // viene posizionata sul punto di partenza dell'utente
    if(!hash || !this.#hashs.includes(hash)) return;
    const triggers = document.querySelectorAll(`a[href$="${hash}"]`);
    const trigger = Array.from(triggers).find(link => !this.contains(link));
    trigger?.scrollIntoView({ block: 'center' });
  }

  get nextArrow() {
    const arrow = this.querySelectorAll(this.dataset.next);
    if(!arrow) {
      console.warn(`Elemento freccia "next" non trovato con selettore: ${this.dataset.next}`);
      return null;
    }
    return arrow;
  }

  get prevArrow() {
    const arrow = this.querySelectorAll(this.dataset.prev);
    if(!arrow) {
      console.warn(`Elemento freccia "prev" non trovato con selettore: ${this.dataset.prev}`);
      return null;
    }
    return arrow;
  }
}
if (!window.customElements.get("catalog-slides")) {
  window.customElements.define("catalog-slides", CatalogSlides);
}